import { getScrollTop, scrollTo, getLenis } from '@theme/scroll-container';
import { isClickedOutside } from '@theme/utilities';

const loadSwiperAssets = (jsUrl, cssUrl) => {
  if (window.Swiper) return Promise.resolve();

  return new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-swiper-css]') && cssUrl) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssUrl;
      link.dataset.swiperCss = '';
      document.head.appendChild(link);
    }

    if (window.Swiper) {
      resolve();
      return;
    }

    const existing = document.querySelector('script[data-swiper-js]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }

    if (!jsUrl) {
      reject(new Error('Swiper JS URL missing'));
      return;
    }

    const script = document.createElement('script');
    script.src = jsUrl;
    script.dataset.swiperJs = '';
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', reject);
    document.head.appendChild(script);
  });
};

const TONE_SAMPLE_RATIO = 0.2;
const TONE_LUMINANCE_THRESHOLD = 0.62;

const toneProbeUrl = (imageUrl) => {
  try {
    const parsed = new URL(imageUrl, window.location.href);
    parsed.searchParams.set('width', '120');
    return parsed.href;
  } catch {
    return imageUrl;
  }
};

const sampleCornerTone = (image, backdrop) => {
  const { naturalWidth: width, naturalHeight: height } = image;
  if (!width || !height) return null;

  const sampleWidth = Math.max(1, Math.round(width * TONE_SAMPLE_RATIO));
  const sampleHeight = Math.max(1, Math.round(height * TONE_SAMPLE_RATIO));

  const canvas = document.createElement('canvas');
  canvas.width = sampleWidth;
  canvas.height = sampleHeight;

  const context = canvas.getContext('2d');
  if (!context) return null;

  context.fillStyle = backdrop;
  context.fillRect(0, 0, sampleWidth, sampleHeight);
  context.drawImage(image, width - sampleWidth, 0, sampleWidth, sampleHeight, 0, 0, sampleWidth, sampleHeight);

  let pixels;
  try {
    pixels = context.getImageData(0, 0, sampleWidth, sampleHeight).data;
  } catch {
    return null;
  }

  let luminanceTotal = 0;
  let samples = 0;

  for (let i = 0; i < pixels.length; i += 4) {
    luminanceTotal += (0.2126 * pixels[i] + 0.7152 * pixels[i + 1] + 0.0722 * pixels[i + 2]) / 255;
    samples += 1;
  }

  if (!samples) return null;

  return luminanceTotal / samples < TONE_LUMINANCE_THRESHOLD ? 'dark' : 'light';
};

class SimucubeGalleryHotspots {
  #root;

  #panel = null;

  #config = [];

  #swiper = null;

  #pill = null;

  #gallery = null;

  #backdrop = null;

  #closeTimer = null;

  #sheetLayoutBound = false;

  #desktopClampBound = false;

  #desktopClampFrame = null;

  #scrollY = 0;

  /** @type {'body-fixed' | 'scroll-lock' | null} */
  #scrollLockMode = null;

  #canonicalSheetTop = null;

  #activeSlideIndex = 0;

  #swiperJsUrl = '';

  #swiperCssUrl = '';

  constructor(root) {
    this.#root = root;

    const configEl = root.querySelector('[data-sc-hotspots-config]');
    if (!configEl) return;

    this.#swiperJsUrl = root.dataset.swiperJs || '';
    this.#swiperCssUrl = root.dataset.swiperCss || '';

    try {
      this.#config = JSON.parse(configEl.textContent || '[]');
    } catch {
      this.#config = [];
    }

    if (!this.#config.length) return;

    const gallery = root.closest('media-gallery');
    this.#gallery = gallery;
    this.#panel = root.querySelector('.sc-hotspot-panel');

    // Anchor the drawer to the full media column, not the (narrower) media
    // item it is rendered inside. media-gallery is sticky, so it is the
    // containing block for the panel's absolute positioning.
    if (this.#panel && gallery) gallery.appendChild(this.#panel);

    // Anchor the "Details" pill to the main image viewport (slideshow-container
    // is position: relative, sits above the thumbnails, and is outside the
    // slides' zoom on:click zone).
    this.#pill = root.querySelector('.sc-hotspot-pill');
    const slideshowContainer = gallery?.querySelector('slideshow-container');
    if (this.#pill && slideshowContainer) {
      slideshowContainer.appendChild(this.#pill);
      this.#pill.hidden = false;
    }

    this.#bindPanelClose();
    this.#bindEscape();
  }

  #bindPanelClose = () => {
    this.#panel?.querySelector('.sc-hotspot-panel__close')?.addEventListener('click', this.#closePanel);
  };

  #bindEscape = () => {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#closePanel();
    });
  };

  // The panel is portaled out of #root on open, so the markers are neither
  // inside it nor its ancestors: the triggers have to be excluded explicitly,
  // otherwise a click on a second marker would slideTo and close at once.
  #handleOutsideClick = (event) => {
    if (!this.#panel || this.#panel.hidden) return;
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.sc-hotspot-panel')) return;
    if (event.target.closest('.sc-hotspot-pill, [data-sc-hotspots-open]')) return;

    if (isClickedOutside(event, this.#panel)) this.#closePanel();
  };

  // Deferred a frame so the click that opened the panel does not reach the
  // listener it just registered.
  #bindOutsideClick = () => {
    requestAnimationFrame(() => {
      if (!this.#panel || this.#panel.hidden) return;
      document.addEventListener('click', this.#handleOutsideClick);
    });
  };

  #unbindOutsideClick = () => {
    document.removeEventListener('click', this.#handleOutsideClick);
  };

  // All hotspot slides render in one carousel; a trigger jumps to that
  // hotspot's first slide.
  #flatSlides = () => this.#config.flatMap((hotspot) => hotspot.slides || []);

  #slideOffset = (hotspotIndex) =>
    this.#config.slice(0, hotspotIndex).reduce((sum, hotspot) => sum + (hotspot.slides?.length || 0), 0);

  // Public entry for external triggers (the "Details" pill, addon "why" link).
  get hasSlides() {
    return this.#flatSlides().length > 0;
  }

  open = (hotspotIndex = 0) => {
    if (!this.hasSlides) return;

    // Cancel any in-flight close so a quick reopen doesn't get finalized away.
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }

    if (this.#panel && !this.#panel.isConnected) {
      this.#panel = this.#root.querySelector('.sc-hotspot-panel');
      if (!this.#panel) return;
      this.#bindPanelClose();
    }

    document.querySelectorAll('.sc-hotspot-panel').forEach((panel) => {
      if (panel !== this.#panel && !panel.hidden) {
        panel.querySelector('.sc-hotspot-panel__close')?.click();
      }
    });

    this.#pill?.setAttribute('aria-expanded', 'true');

    if (this.#panel && !this.#panel.hidden) {
      if (this.#swiper) this.#swiper.slideTo(this.#slideOffset(hotspotIndex));
      if (this.#isMobile()) this.#positionSheet();
      return;
    }

    const mobile = this.#isMobile();
    if (mobile) {
      this.#enterMobileSheet();
    } else if (!this.#gallery && this.#panel) {
      const host = this.#root.closest('.shopify-section')?.querySelector('media-gallery');
      if (host && this.#panel.parentElement !== host) host.appendChild(this.#panel);
    }

    this.#openPanel(hotspotIndex);
    // #openPanel sets panel.hidden = false synchronously before the Swiper await.

    if (mobile) {
      // Next frame: measure sheet offsets, then animate in.
      requestAnimationFrame(() => {
        this.#positionSheet();
        this.#backdrop?.classList.add('sc-hotspot-backdrop--visible');
        this.#panel?.classList.add('sc-hotspot-panel--open');
      });
    } else {
      requestAnimationFrame(this.#clampDesktopPanel);
      this.#bindDesktopClamp();
    }

    this.#panel?.querySelector('.sc-hotspot-panel__close')?.focus();
  };

  #isMobile = () => window.matchMedia('(max-width: 989px)').matches;

  // Mobile: bottom sheet with a dimmed backdrop. Full-page overlay when the header
  // group is hidden (idle); backdrop starts below the header when it is exposed.
  #enterMobileSheet = () => {
    this.#ensureBackdrop();

    if (this.#panel && this.#panel.parentElement !== document.body) {
      document.body.appendChild(this.#panel);
    }

    document.documentElement.classList.add('sc-hotspot-sheet-open');

    const belowHeader = this.#isHeaderGroupExposed();
    document.documentElement.classList.toggle('sc-hotspot-sheet-open--below-header', belowHeader);

    // Measure before body-fixed scroll lock so breadcrumb offsets stay accurate.
    this.#canonicalSheetTop = null;
    this.#positionSheet();

    this.#lockPageScroll(belowHeader);
    this.#bindSheetLayout();

    if (this.#backdrop) this.#backdrop.hidden = false;
  };

  #bindSheetLayout = () => {
    if (this.#sheetLayoutBound) return;
    this.#sheetLayoutBound = true;
    window.addEventListener('resize', this.#onSheetLayoutChange, { passive: true });
    window.addEventListener('orientationchange', this.#onSheetLayoutChange, { passive: true });
  };

  #unbindSheetLayout = () => {
    if (!this.#sheetLayoutBound) return;
    this.#sheetLayoutBound = false;
    window.removeEventListener('resize', this.#onSheetLayoutChange);
    window.removeEventListener('orientationchange', this.#onSheetLayoutChange);
  };

  #onSheetLayoutChange = () => {
    if (!this.#isMobile() || !this.#panel || this.#panel.hidden) return;
    this.#canonicalSheetTop = null;
    this.#positionSheet();
  };

  #clampDesktopPanel = () => {
    if (this.#isMobile() || !this.#panel || this.#panel.hidden) return;

    const { top } = this.#panel.getBoundingClientRect();
    this.#panel.style.setProperty('--sc-panel-max-height', `${Math.max(0, window.innerHeight - top)}px`);
  };

  #bindDesktopClamp = () => {
    if (this.#desktopClampBound) return;
    this.#desktopClampBound = true;
    document.addEventListener('scroll', this.#onDesktopClampChange, { passive: true, capture: true });
    window.addEventListener('resize', this.#onDesktopClampChange, { passive: true });
  };

  #unbindDesktopClamp = () => {
    if (this.#desktopClampFrame) {
      cancelAnimationFrame(this.#desktopClampFrame);
      this.#desktopClampFrame = null;
    }

    if (!this.#desktopClampBound) return;
    this.#desktopClampBound = false;
    document.removeEventListener('scroll', this.#onDesktopClampChange, { capture: true });
    window.removeEventListener('resize', this.#onDesktopClampChange);
  };

  #onDesktopClampChange = () => {
    if (this.#desktopClampFrame) return;

    this.#desktopClampFrame = requestAnimationFrame(() => {
      this.#desktopClampFrame = null;
      this.#clampDesktopPanel();
    });
  };

  #isHeaderGroupExposed = () => {
    const header = document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) return false;

    if (header.dataset.stickyState === 'idle') return false;

    const headerGroupHeight =
      parseInt(getComputedStyle(document.body).getPropertyValue('--header-group-height'), 10) || 0;
    const { top, bottom } = header.getBoundingClientRect();

    return bottom > 0 && top < headerGroupHeight;
  };

  // When the header group is exposed, use body-fixed freeze so Lenis is not stopped
  // (scroll-lock adds lenis-stopped, which breaks position:sticky on the header).
  // Full-screen overlay uses scroll-lock only — header is hidden in that case.
  #lockPageScroll = (preserveStickyHeader = false) => {
    this.#scrollY = getScrollTop();

    if (preserveStickyHeader) {
      if (document.body.style.position === 'fixed') return;

      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      this.#scrollLockMode = 'body-fixed';
      return;
    }

    if (document.documentElement.hasAttribute('scroll-lock')) return;

    document.documentElement.setAttribute('scroll-lock', '');
    this.#scrollLockMode = 'scroll-lock';
  };

  #unlockPageScroll = () => {
    const scrollY = this.#scrollY;
    const mode = this.#scrollLockMode;
    this.#scrollLockMode = null;

    if (mode === 'body-fixed') {
      if (document.body.style.position !== 'fixed') return;

      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('left');
      document.body.style.removeProperty('right');
      document.body.style.removeProperty('width');
      getLenis()?.resize();
      scrollTo({ top: scrollY, behavior: 'instant' });
      return;
    }

    if (mode !== 'scroll-lock' || !document.documentElement.hasAttribute('scroll-lock')) return;

    document.documentElement.removeAttribute('scroll-lock');
    scrollTo({ top: scrollY, behavior: 'instant' });
  };

  #ensureBackdrop = () => {
    if (this.#backdrop) return;

    const backdrop = document.createElement('div');
    backdrop.className = 'sc-hotspot-backdrop';
    backdrop.hidden = true;
    backdrop.addEventListener('click', this.#closePanel);
    document.body.appendChild(backdrop);
    this.#backdrop = backdrop;
  };

  #measureSheetTopAtPageTop = () => {
    const headerGroupHeight =
      parseInt(getComputedStyle(document.body).getPropertyValue('--header-group-height'), 10) || 0;

    const breadcrumbs = document.querySelector('.sc-breadcrumbs--gallery, .sc-breadcrumbs');
    if (!(breadcrumbs instanceof HTMLElement)) return headerGroupHeight || 96;

    const rect = breadcrumbs.getBoundingClientRect();
    const scrollY = this.#scrollLockMode === 'body-fixed' ? this.#scrollY : getScrollTop();
    const breadcrumbBottomAtPageTop = Math.round(rect.bottom + scrollY);

    return Math.max(headerGroupHeight, breadcrumbBottomAtPageTop);
  };

  #positionSheet = () => {
    const belowHeader = this.#isHeaderGroupExposed();
    document.documentElement.classList.toggle('sc-hotspot-sheet-open--below-header', belowHeader);

    if (this.#canonicalSheetTop == null) {
      this.#canonicalSheetTop = this.#measureSheetTopAtPageTop();
    }

    this.#panel?.style.setProperty('--sc-sheet-top', `${this.#canonicalSheetTop}px`);
  };

  #restorePanelHome = () => {
    const home = this.#gallery || this.#root;
    if (this.#panel && this.#panel.parentElement !== home) {
      home.appendChild(this.#panel);
    }
  };

  #openPanel = async (hotspotIndex) => {
    const slides = this.#flatSlides();
    if (!slides.length) return;

    const panel = this.#panel;
    const track = panel?.querySelector('.sc-hotspot-panel__track');
    const paginationEl = panel?.querySelector('.sc-hotspot-panel__pagination');
    if (!panel || !track || !paginationEl) return;

    this.#destroySwiper();
    track.innerHTML = '';
    paginationEl.innerHTML = '';

    slides.forEach((slide) => {
      const slideEl = document.createElement('div');
      slideEl.className = 'sc-hotspot-panel__slide swiper-slide';
      const imageMarkup = slide.image_url
        ? `<div class="sc-hotspot-panel__slide-image-wrap">
          <img class="sc-hotspot-panel__slide-image" src="${this.#escapeAttr(slide.image_url)}" alt="${this.#escapeAttr(slide.title || '')}" loading="lazy" width="400" height="353">
        </div>`
        : '';
      slideEl.innerHTML = `
        ${imageMarkup}
        <div class="sc-hotspot-panel__slide-body">
          <h3 class="sc-hotspot-panel__title">${this.#escapeHtml(slide.title || '')}</h3>
          <p class="sc-hotspot-panel__description">${this.#escapeHtml(slide.description || '')}</p>
        </div>
      `;
      track.appendChild(slideEl);
      this.#detectSlideTone(slideEl, slide.image_url);
    });

    const initialSlide = this.#slideOffset(hotspotIndex);

    panel.hidden = false;
    this.#activeSlideIndex = initialSlide;
    this.#syncCloseTone();
    this.#bindOutsideClick();

    try {
      await loadSwiperAssets(this.#swiperJsUrl, this.#swiperCssUrl);
      if (!window.Swiper) return;

      const viewport = panel.querySelector('.sc-hotspot-panel__viewport');
      viewport?.classList.add('swiper');
      track.classList.add('swiper-wrapper');

      this.#swiper = new window.Swiper(viewport, {
        slidesPerView: 1,
        spaceBetween: 0,
        initialSlide,
        pagination: {
          el: paginationEl,
          clickable: true,
        },
        navigation: {
          prevEl: panel.querySelector('[data-sc-hotspot-prev]'),
          nextEl: panel.querySelector('[data-sc-hotspot-next]'),
          disabledClass: 'simucube-carousel-arrow--disabled',
          lockClass: 'simucube-carousel-arrow--lock',
          hiddenClass: 'simucube-carousel-arrow--hidden',
        },
        on: {
          slideChange: (swiper) => {
            this.#activeSlideIndex = swiper.activeIndex;
            this.#syncCloseTone();
          },
        },
      });
    } catch {
      /* static first slide remains visible */
    }
  };

  #detectSlideTone = (slideEl, imageUrl) => {
    if (!imageUrl) return;

    const wrap = slideEl.querySelector('.sc-hotspot-panel__slide-image-wrap');
    const backdrop = wrap ? getComputedStyle(wrap).backgroundColor : '#ffffff';

    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.addEventListener('load', () => {
      const tone = sampleCornerTone(probe, backdrop);
      if (!tone) return;

      slideEl.dataset.mediaTone = tone;
      this.#syncCloseTone();
    });
    probe.src = toneProbeUrl(imageUrl);
  };

  #syncCloseTone = () => {
    if (!this.#panel) return;

    const slides = this.#panel.querySelectorAll('.sc-hotspot-panel__slide');
    this.#panel.dataset.mediaTone = slides[this.#activeSlideIndex]?.dataset.mediaTone || 'light';
  };

  #closePanel = () => {
    const animateDown = this.#isMobile() && this.#panel && !this.#panel.hidden;

    this.#panel?.classList.remove('sc-hotspot-panel--open');
    this.#backdrop?.classList.remove('sc-hotspot-backdrop--visible');
    this.#pill?.setAttribute('aria-expanded', 'false');

    this.#unbindOutsideClick();

    const finalize = () => {
      this.#closeTimer = null;
      if (this.#panel) this.#panel.hidden = true;
      if (this.#backdrop) this.#backdrop.hidden = true;
      this.#unlockPageScroll();
      document.documentElement.classList.remove('sc-hotspot-sheet-open', 'sc-hotspot-sheet-open--below-header');
      this.#canonicalSheetTop = null;
      this.#unbindSheetLayout();
      this.#unbindDesktopClamp();
      this.#panel?.style.removeProperty('--sc-panel-max-height');
      this.#restorePanelHome();

      this.#destroySwiper();
    };

    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }

    // Let the sheet slide down before tearing it down (transition is 0.3s).
    if (animateDown) this.#closeTimer = window.setTimeout(finalize, 300);
    else finalize();
  };

  #destroySwiper = () => {
    if (!this.#swiper) return;

    this.#swiper.destroy(true, true);
    this.#swiper = null;

    const viewport = this.#panel?.querySelector('.sc-hotspot-panel__viewport');
    const track = this.#panel?.querySelector('.sc-hotspot-panel__track');
    viewport?.classList.remove('swiper');
    track?.classList.remove('swiper-wrapper');
    track?.querySelectorAll('.swiper-slide').forEach((slide) => {
      slide.classList.remove('swiper-slide');
    });
    this.#panel?.querySelectorAll('.simucube-carousel-arrow').forEach((arrow) => {
      arrow.classList.remove(
        'simucube-carousel-arrow--disabled',
        'simucube-carousel-arrow--lock',
        'simucube-carousel-arrow--hidden',
      );
    });
  };

  #escapeHtml = (value) => {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  };

  #escapeAttr = (value) => this.#escapeHtml(value).replace(/"/g, '&quot;');
}

const instances = new WeakMap();

const initGalleryHotspots = (scope = document) => {
  scope.querySelectorAll('[data-sc-hotspots]:not([data-sc-hotspots-init])').forEach((root) => {
    root.dataset.scHotspotsInit = '';
    instances.set(root, new SimucubeGalleryHotspots(root));
  });
};

initGalleryHotspots();

// Delegated triggers: the mobile "Details" pill and the addon "why" link share
// [data-sc-hotspots-open]. Module scripts dedupe by URL, so this listener binds
// exactly once even when the snippet renders in several sections.
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const trigger = event.target.closest('[data-sc-hotspots-open]');
  if (!trigger) return;

  // Preserve open-in-new-tab intent on the anchor trigger.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const scope = trigger.closest('media-gallery') || trigger.closest('.shopify-section, dialog') || document;
  const targetId = trigger.getAttribute('data-sc-hotspots-target');
  const root = targetId ? document.getElementById(targetId) : scope.querySelector('.product-media[data-sc-hotspots]');
  if (!root) return; // no hotspots gallery in scope: anchor falls through to its href

  if (!instances.get(root)) initGalleryHotspots(scope); // lazily re-init after a gallery replacement
  const instance = instances.get(root);
  if (!instance?.hasSlides) return; // fall through to href

  event.preventDefault();
  instance.open(Number(trigger.dataset.scHotspotsOpen) || 0);
});

// MediaGallery replaces itself wholesale on shopify:product:select (variant
// change), dropping init state and the re-parented pill/panel; re-init afterward.
document.addEventListener('shopify:product:select', (event) => {
  const promise = event.promise instanceof Promise ? event.promise : Promise.resolve();
  promise.then(() => requestAnimationFrame(() => initGalleryHotspots())).catch(() => {});
});

document.addEventListener('shopify:section:load', (event) => {
  initGalleryHotspots(event.target);
});
